import {
  Camera,
  Intersection,
  Object3D,
  Raycaster,
  Scene,
  Vector2,
  Vector3,
} from "three";
import { OBB } from "three/examples/jsm/math/OBB";
import { Engine } from "../engine/Engine";
import { SugarModel } from "../loader/SugarModel";
import { SugarModelBase } from "../loader/SugarModelLoader";
import { Balcony } from "../shape/walls/Balcony";
import { BlankWall } from "../shape/walls/BlankWall";
import { Wall } from "../shape/walls/Wall";

export const boxCast = (
  sourceObb: OBB,
  direction: Vector3,
  object: Object3D
) => {
  const corners = getObbWorldCorners(sourceObb);

  const raycaster = new Raycaster();
  let minDistance = Infinity;
  let hitPoint = new Vector3();

  corners.forEach((corner) => {
    const toCenter = corner.clone().sub(sourceObb.center);
    const dot = toCenter.dot(direction);
    if (dot >= 0) {
      raycaster.set(corner, direction);
      const intersects = raycaster.intersectObject(object, true);

      if (intersects.length > 0) {
        const dist = intersects[0].distance;
        if (dist < minDistance) {
          minDistance = dist;
          hitPoint = intersects[0].point.clone();
        }
      }
    }
  });

  return {
    point: hitPoint,
    distance: minDistance,
  };
};


export const getObbWorldCorners = (obb: OBB): Vector3[] => {
  const corners: Vector3[] = [];
  for (let sx = -1; sx <= 1; sx += 2) {
    for (let sy = -1; sy <= 1; sy += 2) {
      for (let sz = -1; sz <= 1; sz += 2) {
        const local = new Vector3(
          sx * obb.halfSize.x,
          sy * obb.halfSize.y,
          sz * obb.halfSize.z
        );
        corners.push(local.applyMatrix3(obb.rotation).add(obb.center.clone()));
      }
    }
  }
  return corners;
};

export const getClosestCorner = (obb: OBB, point: Vector3): Vector3 => {
  const corners = getObbWorldCorners(obb);
  let minDist = Infinity;
  let closest = corners[0];
  for (const corner of corners) {
    const dist = corner.distanceTo(point);
    if (dist < minDist) {
      minDist = dist;
      closest = corner;
    }
  }
  return closest;
};

export const getModelObject = (
  object: Object3D
): SugarModelBase | BlankWall | Wall | Balcony | undefined => {
  if ((object as any) instanceof SugarModelBase && !(object as any).freeze) {
    return object as SugarModelBase;
  } else if ((object as any).name.startsWith("Root")) {
    return object as any as BlankWall | Wall | Balcony;
  }

  if (object.parent != undefined && !(object.parent instanceof Scene)) {
    return getModelObject(object.parent);
  }
  return undefined;
};

/** Dar compound OBB’lerde yüzey toleransı (grid kaba olduğunda yanlış red azaltır). */
const MODEL_PICK_OBB_MARGIN = 1.04;

export function hitPointInsideModelCompoundObbs(
  model: SugarModel,
  worldPoint: Vector3,
): boolean {
  for (const w of model.obbs) {
    const o = w.clone();
    o.halfSize.multiplyScalar(MODEL_PICK_OBB_MARGIN);
    if (o.containsPoint(worldPoint)) return true;
  }
  return false;
}

/**
 * Ray sırasına göre ilk geçerli isabet: SugarModel ise isabet noktası compound OBB birleşiminde olmalı
 * (geniş kapsayıcı / boş L içi yanlış seçim engellenir). Duvar vb. için ilk isabet aynen kullanılır.
 */
export function pickIntersectionForSceneSelection(
  intersections: Intersection[] | undefined,
): Intersection | undefined {
  if (!intersections?.length) return undefined;
  for (const hit of intersections) {
    const owner = getModelObject(hit.object);
    if (owner instanceof SugarModel) {
      if (hitPointInsideModelCompoundObbs(owner, hit.point)) return hit;
      continue;
    }
    return hit;
  }
  return undefined;
}

export const getModelObjectRoot = (
  object: Object3D,
  expression: (object: Object3D) => boolean
): Object3D | null => {
  let p: Object3D | null = object;
  let r = null;
  while (p) {
    if (expression(p)) r = p;
    p = p.parent;
  }

  return r;
};

export const getModelObjectFirst = (
  object: Object3D,
  expression: (object: Object3D) => boolean
): Object3D | null => {
  let p: Object3D | null = object;
  while (p && !expression(p)) {
    p = p.parent;
  }

  return p;
};

const lastTouchVector: Vector2 = new Vector2();

const getCoordsOnRect = (
  event: DragEvent | MouseEvent | PointerEvent | Touch | TouchEvent,
  rect: DOMRect
) => {
  try {
    if (typeof TouchEvent !== "undefined" && event instanceof TouchEvent) {
      event = event.touches[0] ?? event.changedTouches[0];
    }
  } catch {}

  if (!event) return lastTouchVector;

  const clientX = (event as any).clientX
    ? (event as any).clientX
    : (event as any).detail.clientX;
  const clientY = (event as any).clientY
    ? (event as any).clientY
    : (event as any).detail.clientY;

  if (clientX == undefined || clientY == undefined) return lastTouchVector;
  if (rect == undefined) return lastTouchVector;
  if (rect.width == 0 || rect.height == 0) return lastTouchVector;
  if (rect.left == undefined || rect.top == undefined) return lastTouchVector;
  if (rect.width == undefined || rect.height == undefined)
    return lastTouchVector;

  const screenX = ((clientX - rect.left) / rect.width) * 2.0 - 1.0;
  const screenY = ((clientY - rect.top) / rect.height) * -2.0 + 1.0;
  const coords = new Vector2(screenX, screenY);
  lastTouchVector.set(coords.x, coords.y);
  return coords;
};

const getCoordsOnDom = (
  event: MouseEvent | PointerEvent | Touch | DragEvent | TouchEvent,
  dom: HTMLElement
) => {
  const rect = dom.getBoundingClientRect();
  return getCoordsOnRect(event, rect);
};

const raycastOnPlaneFromScreen = (
  event: MouseEvent | PointerEvent | Touch | DragEvent | TouchEvent,
  plane: { position: Vector3; normal: Vector3 },
  camera: Camera,
  dom: HTMLElement
) => {
  const coords = getCoordsOnDom(event, dom);
  const raycaster = new Raycaster();
  raycaster.setFromCamera(coords, camera);
  return raycastOnPlane(raycaster.ray, plane);
};

const raycastOnPlane = (
  ray: { origin: Vector3; direction: Vector3 },
  plane: { position: Vector3; normal: Vector3 }
) => {
  const distanceVector = plane.position.clone().sub(ray.origin);
  const lenght = distanceVector.length();
  const cosAngle = distanceVector.clone().normalize().dot(plane.normal);
  const cosAngle2 = ray.direction.clone().dot(plane.normal);

  return ray.origin
    .clone()
    .add(ray.direction.clone().multiplyScalar((lenght * cosAngle) / cosAngle2));
};

const raycastCoordsCameraOBB = (
  coords: Vector2,
  camera: Camera,
  scene: OBB
) => {
  let raycaster = new Raycaster();
  raycaster.setFromCamera(coords, camera);

  const result = new Vector3();
  return scene.intersectRay(raycaster.ray, result);
};

const raycastCoordsCameraObject = (
  coords: Vector2,
  camera: Camera,
  scene: Object3D | Object3D[]
) => {
  let raycaster = new Raycaster();
  raycaster.setFromCamera(coords, camera);

  if (Array.isArray(scene)) {
    return raycaster.intersectObjects(scene, true);
  }
  return raycaster.intersectObject(scene!, true);
};

const raycast = (
  coords: Vector2 | MouseEvent | PointerEvent | Touch | DragEvent | TouchEvent,
  scene?: Object3D | Object3D[],
  camera?: Camera,
  dom?: HTMLElement
) => {
  if (!camera) {
    camera = Engine.activeEngine.activeCamera!.instance;
  }
  if (!scene) {
    scene = Engine.activeEngine.activeScene!;
  }
  if (!dom) {
    dom = Engine.activeEngine.canvas as HTMLElement;
  }

  if (!(coords instanceof Vector2)) {
    coords = getCoordsOnDom(coords, dom);
  }

  return raycastCoordsCameraObject(coords, camera, scene);
};

export const raycastOnObb = (
  coords: Vector2 | MouseEvent | PointerEvent | Touch | DragEvent | TouchEvent,
  obbs: OBB,
  camera?: Camera,
  dom?: HTMLElement
) => {
  if (!camera) {
    camera = Engine.activeEngine.activeCamera!.instance;
  }

  if (!dom) {
    dom = Engine.activeEngine.canvas as HTMLElement;
  }

  if (!(coords instanceof Vector2)) {
    coords = getCoordsOnDom(coords, dom);
  }

  return raycastCoordsCameraOBB(coords, camera, obbs);
};

export const raycastOnModel = (
  coords: Vector2 | MouseEvent | Touch | DragEvent,
  scene?: Object3D | Object3D[],
  camera?: Camera,
  dom?: HTMLElement
) => {
  const intersections = raycast(coords, scene, camera, dom);
  if (intersections && intersections.length > 0)
    return getModelObject(intersections[0].object);
  return undefined;
};

export { raycast, raycastOnPlane, raycastOnPlaneFromScreen };
